import { check } from 'meteor/check';
import { Meteor } from 'meteor/meteor';
import { ensureIndex } from '/imports/api/ensureIndex';
import { Mongo } from 'meteor/mongo';
import { Random } from 'meteor/random';
import { DeclareMethod } from '/imports/methods';
import { currentUserId, filterBoolean, uniqueArray } from '/imports/utils';

export type LogLevel = 'trace' | 'info' | 'warning' | 'error' | 'critical';

export interface Log {
  _id: string;
  level: LogLevel;
  message: string;
  data?: any;
  extra?: any;
  orgId?: string;
  timestamp: Date,
  clientTimestamp?: Date,
}

export const Logs = new Mongo.Collection<Log>('logs');

/** How long a log line lives. This collection is unbounded otherwise: `log()`
 *  is called per REST request by `endpoint()` and throughout the bot engine,
 *  and it reached 207M documents / 30GB before anything trimmed it. Mongo's TTL
 *  monitor enforces this continuously once the index below exists. */
export const LOG_RETENTION_DAYS = 30;


// Fire-and-forget IIFE, NOT Meteor.startup: startup hooks are awaited before
// the server listens and a rejecting one calls process.exit(1). Index work on
// the largest collection in the database must never be able to stall or kill
// boot. Same pattern as escrows/musers/tradeItems.
Meteor.isServer && (async () => {
  await ensureIndex(Logs, { level: 1 });
  // Named explicitly so this definition, the retention migration, and whatever
  // is already deployed all converge on one index rather than fighting.
  await ensureIndex(Logs, { timestamp: 1 }, {
    name: 'timestamp_ttl',
    expireAfterSeconds: LOG_RETENTION_DAYS * 86400,
  });
  await ensureIndex(Logs, { 'extra.tags': 1 }, { sparse: true });
})();

export type ListLogsArg = {
  limit: number,
  tags?: string[],
  levels?: string[],
}
export const listLogs = DeclareMethod<ListLogsArg, Log[]>('listLogs');

export const logData = DeclareMethod<string, any>('logData');

export const logSearchQuery = (search?: string) => {
  search = search?.trim();
  if (!search?.length) return {};
  return {
    $or: [
      { message: new RegExp(search, 'gi') },
    ]
  };
};

export const canListLogs = (user?: Meteor.User | null) => {
  if (!user) return false;
  if (user?.isSuperUser) return true;
  return false;
};

export const log = (
  level: LogLevel,
  message: string,
  data?: any,
  extra?: {
    userId?: string,
    orgId?: string,
    tags?: string[],
    data?: any,
  }
) => {
  const timestamp = new Date();
  const _id = Random.id();
  const userId = currentUserId();
  console.log(`${timestamp.toISOString()} [${level}] ${message}${data ? `: ${JSON.stringify(data)}` : ''}`);
  extra = extra || {};
  if (userId) {
    extra.tags = extra.tags || [];
    extra.tags.push(`u-${userId}`);
  }
  if (extra.tags) extra.tags = uniqueArray(extra.tags);
  return Logs.rawCollection().insertOne({
    _id, level, message, data, extra, timestamp,
  }, { writeConcern: { w: 0 } });
};

export const listLogsCursorParams = (arg: ListLogsArg) => {
  const { limit, levels, tags } = arg || {};
  limit && check(limit, Number);
  tags && check(tags, [String]);
  levels && check(levels, [String]);
  const startTime = new Date();
  startTime.setDate(startTime.getDate() - 1);
  const query: any = { $and: [{}] };
  const _tags = tags ? uniqueArray(filterBoolean(tags)) : undefined;
  if (_tags?.length) query.$and.push({ 'extra.tags': { $all: _tags } });
  query.$and.push({ level: levels?.length ? { $in: levels } : { $exists: true } });
  const sort = { timestamp: -1 };
  const fields = { data: 0 };
  return { query, sort, limit, fields };
};